import { Link } from 'react-router-dom';
import { Button, Card, CardHeader } from '@/components/common';
import type { Policy } from '@/types';
import { MEMBER_RELATIONS } from '@/pages/policies/healthStructure';

const relationLabel = (v: string) => MEMBER_RELATIONS.find((r) => r.value === v)?.label ?? v;

/**
 * Read-only insured members (and master policy, for group health) on the policy
 * detail page (#332). Editing goes through the Edit Policy form, which renders
 * the same member block. Members are health PII, so nothing here is logged or
 * echoed anywhere else.
 */
export function PolicyMembersCard({ policy, editHref }: { policy: Policy; editHref?: string }) {
  const members = policy.members ?? [];
  const isGroup = policy.member_structure?.mode === 'group';
  const hasMaster = !!(policy.master_policy_number || policy.master_policy_name);

  return (
    <Card>
      <CardHeader
        title={isGroup ? 'Master Policy & Insured Members' : 'Insured Members'}
        action={
          editHref ? (
            <Link to={editHref}>
              <Button variant="outline" size="sm">
                Edit members
              </Button>
            </Link>
          ) : undefined
        }
      />
      {(isGroup || hasMaster) && (
        <dl className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm text-gray-500 dark:text-gray-400">Master policy number</dt>
            <dd className="text-gray-900 dark:text-white">{policy.master_policy_number || '—'}</dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500 dark:text-gray-400">Master policy name</dt>
            <dd className="text-gray-900 dark:text-white">{policy.master_policy_name || '—'}</dd>
          </div>
        </dl>
      )}
      {members.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No insured members recorded</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="py-2 pr-4 font-medium">Name</th>
                <th className="py-2 pr-4 font-medium">Relation</th>
                <th className="py-2 pr-4 font-medium">Date of birth</th>
                <th className="py-2 pr-4 font-medium">Gender</th>
                <th className="py-2 pr-4 font-medium">Pre-existing conditions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-t border-gray-200 text-gray-900 dark:border-gray-700 dark:text-white">
                  <td className="py-2 pr-4">{m.name}</td>
                  <td className="py-2 pr-4">{relationLabel(m.relation)}</td>
                  <td className="py-2 pr-4">{m.dob}</td>
                  <td className="py-2 pr-4 capitalize">{m.gender}</td>
                  <td className="py-2 pr-4">{m.pre_existing_diseases?.join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default PolicyMembersCard;
